import React from 'react';
import CapitalActionCard from './CapitalActionCard';
import { CommitmentType, CurrencyCode } from 'further-types/lp/vehicle';
import { LinkedCapitalAction } from 'further-types/lp/capital-action';
import EqualisationFooter from '../EqualisationFooter';
import { useParams } from 'react-router-dom';
import { useLayoutContext } from 'contexts/LayoutContext';
import { useEqualisationSection } from '../EqualisationContext';
import api from 'lib/trpcClient';
import { EqualisationSection } from 'further-types/lp/equalisation';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { getQueryKey } from '@trpc/react-query';
import { Alert, Typography } from '@mui/material';
import EqualisationPage from '../EqualisationPage';
import Table, { Column as TableColumn } from 'further-ui/components/Table';
import { numberToCurrencyString } from 'further-ui/utils';
import { useExpandedRows } from 'hooks/ui/useExpandedRows';
import { Column } from 'further-ui/components/Layout';

interface ActionLinkedCapitalActionsContentProps {
  capitalActions: Array<LinkedCapitalAction>;
  baseCurrency: CurrencyCode;
}

const ActionLinkedCapitalActionsContent: React.FC<
  ActionLinkedCapitalActionsContentProps
> = ({ capitalActions, baseCurrency }) => {
  const {
    previousSection,
    nextSection,
    currentSection,
    goToNextSection,
    goToPreviousSection,
    goToListPage,
  } = useEqualisationSection();
  const { id } = useParams<{ id?: string }>();
  const { selectedVehicleId, vehicle } = useLayoutContext();
  const { expandedRows, toggleRowExpansion } = useExpandedRows();

  const equalisationActionAllocationsQueryKey = getQueryKey(
    api.equalisations.getLinkedCapitalActions,
    {
      equalisationId: id ?? '',
      vehicleId: selectedVehicleId ?? '',
    },
  );

  const updateEqualisation = api.equalisations.update.useMutation(
    useHandleTrpcMutation()({
      invalidations: [equalisationActionAllocationsQueryKey],
    }),
  );

  const handleNext = () => {
    updateEqualisation.mutate(
      {
        equalisationId: id ?? '',
        vehicleId: selectedVehicleId ?? '',
        currentSection: nextSection ?? EqualisationSection.Summary,
        saveActionLinkedCapitalActions: true,
      },
      {
        onSuccess: () => {
          goToNextSection();
        },
      },
    );
  };

  const handleSaveAsDraft = () => {
    updateEqualisation.mutate(
      {
        equalisationId: id ?? '',
        vehicleId: selectedVehicleId ?? '',
        currentSection,
        saveActionLinkedCapitalActions: true,
      },
      {
        onSuccess: () => {
          goToListPage();
        },
      },
    );
  };

  const handlePrevious = () => {
    updateEqualisation.mutate(
      {
        equalisationId: id ?? '',
        vehicleId: selectedVehicleId ?? '',
        currentSection: previousSection ?? EqualisationSection.Commitments,
      },
      {
        onSuccess: () => {
          goToPreviousSection();
        },
      },
    );
  };

  const columns: Array<TableColumn<(typeof capitalActions)[number]>> = [
    {
      key: 'name',
      label: 'Name',
      render: (row) => row.name,
      renderColorIndicator: ({ name }) => ({
        colorKey: { name },
        content: (
          <Column>
            <Typography variant="inherit">{name}</Typography>
          </Column>
        ),
      }),
    },
    {
      key: 'ledgerDate',
      label: 'Date',
      render: (row) => row.ledgerDate.toReadable(),
    },
    {
      key: 'totalAmountLcy',
      label: 'Amount',
      render: (row) =>
        numberToCurrencyString(row.totalAmountLcy, { currency: baseCurrency }),
    },
  ];

  return (
    <EqualisationPage
      actionBar={
        <EqualisationFooter
          handleNext={handleNext}
          handlePrevious={handlePrevious}
          handleSaveAsDraft={handleSaveAsDraft}
          loading={updateEqualisation.isPending}
        />
      }
      subheading="Equalise action-linked capital calls."
    >
      {vehicle?.commitmentType === CommitmentType.CapitalAndLoan && (
        <Alert severity="info">
          This step will calculate the total gross amount to be called from each
          investor. Where an investor has a capital contribution to be called,
          their capital contribution will be deducted from their total gross
          call amount.
        </Alert>
      )}
      <Table
        isExpandableRow={() => true}
        isExpandedRow={({ id }) => expandedRows.has(id)}
        onRowExpand={({ id }) => {
          toggleRowExpansion(id);
        }}
        onRowClick={({ id }) => {
          toggleRowExpansion(id);
        }}
        getExpandedContent={(row) => (
          <CapitalActionCard key={row.id} capitalAction={row} />
        )}
        columns={columns}
        rows={capitalActions}
      />
    </EqualisationPage>
  );
};

export default ActionLinkedCapitalActionsContent;
